import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { AlertCircle, Brain, Heart, Zap, CheckCircle, RotateCcw } from 'lucide-react';

// Questions basées sur l'échelle de Maslach adaptée
const questions = [
  {
    id: 1,
    category: 'Épuisement émotionnel',
    icon: <Heart className="w-5 h-5" />,
    question: "Je me sens émotionnellement vidé(e) par mon travail",
    dimension: 'exhaustion'
  },
  {
    id: 2,
    category: 'Épuisement émotionnel',
    icon: <Heart className="w-5 h-5" />,
    question: "Je me sens épuisé(e) à la fin de ma journée de travail",
    dimension: 'exhaustion'
  },
  {
    id: 3,
    category: 'Épuisement émotionnel',
    icon: <Heart className="w-5 h-5" />,
    question: "Je me sens fatigué(e) dès que je me lève et que je pense à ma journée de travail",
    dimension: 'exhaustion'
  },
  {
    id: 4,
    category: 'Dépersonnalisation',
    icon: <Brain className="w-5 h-5" />,
    question: "Je traite certaines personnes comme si elles étaient des objets impersonnels",
    dimension: 'depersonalization'
  },
  {
    id: 5,
    category: 'Dépersonnalisation',
    icon: <Brain className="w-5 h-5" />,
    question: "Je me soucie peu de ce qui arrive à certaines personnes dans mon travail",
    dimension: 'depersonalization'
  },
  {
    id: 6,
    category: 'Accomplissement personnel',
    icon: <Zap className="w-5 h-5" />,
    question: "Je résous efficacement les problèmes dans mon travail",
    dimension: 'accomplishment'
  },
  {
    id: 7,
    category: 'Accomplissement personnel',
    icon: <Zap className="w-5 h-5" />,
    question: "J'ai une influence positive sur la vie des gens grâce à mon travail",
    dimension: 'accomplishment'
  },
  {
    id: 8,
    category: 'Épuisement émotionnel',
    icon: <Heart className="w-5 h-5" />,
    question: "Travailler avec des gens me stresse vraiment",
    dimension: 'exhaustion'
  },
  {
    id: 9,
    category: 'Accomplissement personnel',
    icon: <Zap className="w-5 h-5" />,
    question: "Je me sens plein(e) d'énergie dans mon travail",
    dimension: 'accomplishment'
  },
  {
    id: 10,
    category: 'Épuisement émotionnel',
    icon: <Heart className="w-5 h-5" />,
    question: "Je me sens frustré(e) par mon travail",
    dimension: 'exhaustion'
  },
  {
    id: 11,
    category: 'Dépersonnalisation',
    icon: <Brain className="w-5 h-5" />,
    question: "Je pense que ce travail me durcit émotionnellement",
    dimension: 'depersonalization'
  },
  {
    id: 12,
    category: 'Accomplissement personnel',
    icon: <Zap className="w-5 h-5" />,
    question: "Je peux facilement comprendre ce que ressentent mes collègues/clients",
    dimension: 'accomplishment'
  }
];

const responseOptions = [
  { value: 0, label: 'Jamais', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 1, label: 'Quelques fois par an', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 2, label: 'Une fois par mois', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 3, label: 'Quelques fois par mois', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 4, label: 'Une fois par semaine', color: 'bg-red-100 text-red-800 border-red-200' },
  { value: 5, label: 'Quelques fois par semaine', color: 'bg-red-200 text-red-900 border-red-300' },
  { value: 6, label: 'Chaque jour', color: 'bg-red-300 text-red-900 border-red-400' }
];

const Index = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');

  const handleAnswer = (value: number) => {
    setAnswers(prev => ({ ...prev, [questions[currentQuestion].id]: value }));
    setSelectedAnswer('');
    
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      } else {
        setShowResults(true);
      }
    }, 500);
  };

  const calculateResults = () => {
    let exhaustionScore = 0;
    let depersonalizationScore = 0;
    let accomplishmentScore = 0;

    questions.forEach(q => {
      const answer = answers[q.id] || 0;
      if (q.dimension === 'exhaustion') {
        exhaustionScore += answer;
      } else if (q.dimension === 'depersonalization') {
        depersonalizationScore += answer;
      } else if (q.dimension === 'accomplishment') {
        // Pour l'accomplissement, on inverse le score (plus c'est haut, mieux c'est)
        accomplishmentScore += (6 - answer);
      }
    });

    return { exhaustionScore, depersonalizationScore, accomplishmentScore };
  };

  const getBurnoutLevel = () => {
    const { exhaustionScore, depersonalizationScore, accomplishmentScore } = calculateResults();
    
    // Seuils adaptés pour notre version courte
    const highExhaustion = exhaustionScore >= 15;
    const highDepersonalization = depersonalizationScore >= 8;
    const lowAccomplishment = accomplishmentScore <= 15;

    if (highExhaustion && highDepersonalization && lowAccomplishment) {
      return { level: 'Risque élevé', color: 'text-red-600', bgColor: 'bg-red-50', icon: <AlertCircle className="w-6 h-6 text-red-600" /> };
    } else if ((highExhaustion && highDepersonalization) || (highExhaustion && lowAccomplishment)) {
      return { level: 'Risque modéré', color: 'text-orange-600', bgColor: 'bg-orange-50', icon: <AlertCircle className="w-6 h-6 text-orange-600" /> };
    } else if (highExhaustion || highDepersonalization || lowAccomplishment) {
      return { level: 'Risque faible', color: 'text-yellow-600', bgColor: 'bg-yellow-50', icon: <AlertCircle className="w-6 h-6 text-yellow-600" /> };
    } else {
      return { level: 'Bien-être', color: 'text-green-600', bgColor: 'bg-green-50', icon: <CheckCircle className="w-6 h-6 text-green-600" /> };
    }
  };

  const getRecommendations = () => {
    const burnoutLevel = getBurnoutLevel();
    
    switch (burnoutLevel.level) {
      case 'Risque élevé':
        return [
          'Consultez un professionnel de santé mentale',
          'Envisagez un arrêt de travail temporaire',
          'Parlez à votre employeur de votre situation',
          'Pratiquez des techniques de relaxation quotidiennes'
        ];
      case 'Risque modéré':
        return [
          'Améliorez votre équilibre vie professionnelle/personnelle',
          'Pratiquez une activité physique régulière',
          'Apprenez des techniques de gestion du stress',
          'Cherchez du soutien auprès de vos proches'
        ];
      case 'Risque faible':
        return [
          'Maintenez vos bonnes habitudes actuelles',
          'Restez vigilant(e) aux signaux de stress',
          'Continuez à prendre soin de vous',
          'Cultivez vos relations sociales'
        ];
      default:
        return [
          "Félicitations ! Vous êtes en bonne santé mentale",
          "Continuez à maintenir cet équilibre",
          "Partagez vos bonnes pratiques avec vos collègues",
          "Restez à l'écoute de votre bien-être"
        ];
    }
  };

  const resetTest = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setSelectedAnswer('');
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (showResults) {
    const { exhaustionScore, depersonalizationScore, accomplishmentScore } = calculateResults();
    const burnoutLevel = getBurnoutLevel();
    const recommendations = getRecommendations();

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 animate-slide-in">
            <div className="inline-flex items-center gap-2 mb-4">
              {burnoutLevel.icon}
              <h1 className="text-3xl font-bold text-gray-800">Résultats de votre évaluation</h1>
            </div>
            <p className="text-gray-600">Analyse complète de votre niveau de burn-out</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className={`${burnoutLevel.bgColor} border-2 animate-slide-in`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${burnoutLevel.color}`}>
                  {burnoutLevel.icon}
                  Niveau de burn-out
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${burnoutLevel.color} mb-2`}>
                  {burnoutLevel.level}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Épuisement émotionnel:</span>
                    <Badge variant="outline">{exhaustionScore}/30</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Dépersonnalisation:</span>
                    <Badge variant="outline">{depersonalizationScore}/12</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Accomplissement:</span>
                    <Badge variant="outline">{accomplishmentScore}/24</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="animate-slide-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-600">
                  <Brain className="w-5 h-5" />
                  Recommandations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button onClick={resetTest} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full">
              <RotateCcw className="w-4 h-4 mr-2" />
              Refaire le test
            </Button>
            <p className="text-xs text-gray-500 mt-4">
              Ce test est à des fins informatives uniquement. Consultez un professionnel pour un diagnostic complet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-8 animate-slide-in">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="p-3 bg-blue-100 rounded-full animate-pulse-soft">
              <Brain className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Test d'évaluation du burn-out</h1>
          </div>
          <p className="text-gray-600 mb-6">
            Un outil scientifique pour évaluer votre niveau de stress professionnel
          </p>
          
          {/* Barre de progression */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Question {currentQuestion + 1} sur {questions.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-3 bg-gray-200" />
          </div>
        </div>

        {/* Carte de question */}
        <Card className="question-card animate-slide-in mb-6">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                {questions[currentQuestion].icon}
              </div>
              <Badge variant="outline" className="text-xs">
                {questions[currentQuestion].category}
              </Badge>
            </div>
            <CardTitle className="text-xl text-gray-800">
              {questions[currentQuestion].question}
            </CardTitle>
            <CardDescription>
              Sélectionnez la fréquence qui correspond le mieux à votre situation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={selectedAnswer}
              onValueChange={setSelectedAnswer}
              className="space-y-3"
            >
              {responseOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-3">
                  <RadioGroupItem
                    value={option.value.toString()}
                    id={`option-${option.value}`}
                    className="text-blue-600"
                  />
                  <Label
                    htmlFor={`option-${option.value}`}
                    className={`flex-1 p-3 rounded-lg border-2 cursor-pointer transition-all answer-button ${
                      selectedAnswer === option.value.toString()
                        ? option.color + ' border-current'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            
            <div className="mt-6 text-center">
              <Button
                onClick={() => handleAnswer(parseInt(selectedAnswer))}
                disabled={!selectedAnswer}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentQuestion === questions.length - 1 ? 'Voir les résultats' : 'Question suivante'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Indicateurs de progression */}
        <div className="flex justify-center space-x-2">
          {questions.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full transition-all ${
                index <= currentQuestion
                  ? 'bg-blue-600 animate-pulse-soft'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Index;
